import {
    AlertCircleOutline,
    CashOutline,
    CheckmarkCircle,
    MegaphoneOutline,
    NotificationsOutline,
    RefreshOutline,
    TimeOutline,
    WalletOutline,
  } from "@raresail/react-ionicons";
  import { Tag } from "antd";
  import { useEffect } from "react";
  import { TListHook } from "../../@types/common";
  import { Button } from "../../components/common/Button";
  import {
    IconBox,
    ListItemWrapper,
  } from "../../components/elements/Elements";
  import { API_NOTIFICATIONS } from "../../constants/apis";
  import {
    NOTIFICATIONS,
    SELECT,
  } from "../../constants/constants";
  import { searchFilterField } from "../../constants/options";
  import { addSelectOption, formatDate, stringify } from "../../utils/helpers";
  import { openBottomSheet } from "../../utils/modalUtils";
  import useSet from "../common/useSet";
  import { reloadData } from "../../components/lists/ListHandler";
  import { updateGlobalContext } from "../../components/context/GlobalContext";
  import { PRIMARY_COLOR, primary10, primary5 } from "../../constants/colors";
  
  // Icon mapping by notification type
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "transaction":
        return CashOutline;
      case "loan_update":
        return WalletOutline;
      case "investment_update":
        return RefreshOutline;
      case "repayment_reminder":
        return TimeOutline;
      case "penalty":
        return AlertCircleOutline;
      case "device_order":
        return MegaphoneOutline;
      default:
        return NotificationsOutline;
    }
  };
  
  const getTypeColor = (type: string) => {
    switch (type) {
      case "transaction":
        return "blue";
      case "loan_update":
        return "orange";
      case "investment_update":
        return "green";
      case "repayment_reminder":
        return "gold";
      case "penalty":
        return "red";
      case "system":
        return "purple";
      default:
        return "default";
    }
  };
  
  export const useNotificationsList = (): TListHook => {
    const filters = [
      searchFilterField,
      {
        field: "notification_type",
        type: SELECT,
        options: [
          "transaction",
          "loan_update",
          "investment_update",
          "repayment_reminder",
          "system",
          "product",
          "penalty",
          "device_order",
        ].map((v) => addSelectOption(v)),
      },
      {
        field: "is_read",
        type: SELECT,
        options: [
          { id: "true", name: "Read" },
          { id: "false", name: "Unread" },
        ],
      },
    ];
  
    const viewData = (data: any) => {
      const {
        title,
        message,
        notification_type,
        is_read,
        read_at,
        created_at,
      } = data || {};
      return {
        title,
        message,
        type: (
          <Tag color={getTypeColor(notification_type)} className="font11 capitalize" style={{ margin: 0 }}>
            {stringify(notification_type)}
          </Tag>
        ),
        read: is_read ? "Yes" : "No",
        ...(read_at ? { read_at: formatDate(read_at) } : {}),
        created_at: formatDate(created_at),
      };
    };
  
    const ListItem = ({ data }: { data: any }) => {
      const {
        id,
        title,
        message,
        notification_type,
        is_read,
        created_at,
      } = data || {};
  
      const Icon = getNotificationIcon(notification_type);
  
      return (
        <ListItemWrapper
          className="flexNullCenter gap15"
          style={{
            opacity: is_read ? 0.7 : 1,
          }}
          onClick={() =>
            openBottomSheet({
              component: "details",
              title: "Notification",
              source: NOTIFICATIONS,
              data,
              // Pass the raw row as `state` so the footer can access the id
              // — viewData strips `id` from the displayed object, so we
              // can't rely on it being in the `data` prop the footer receives.
              state: data,
              footer: NotificationFooter,
            })
          }
        >
          {/* ICON */}
          <div
            className="r15 p10 flexCenter"
            style={{
              background: is_read ? primary5 : primary10,
              color: PRIMARY_COLOR,
            }}
          >
            <Icon fontSize={18} />
          </div>
  
          {/* CONTENT */}
          <div className="flexGrow" style={{ minWidth: 0 }}>
            <div className="flexNullCenter gap8 mb3">
              <div
                className={`font13 textDark ellipsis ${
                  !is_read ? "semiBold" : ""
                }`}
              >
                {title}
              </div>
              {!is_read && (
                <div
                  className="r20"
                  style={{
                    width: 7,
                    height: 7,
                    background: PRIMARY_COLOR,
                    flexShrink: 0,
                  }}
                />
              )}
            </div>
            <div className="font11 grey ellipsis">{message}</div>
            <div className="font10 grey mt3">{formatDate(created_at)}</div>
          </div>
  
          {/* TYPE TAG */}
          <Tag
            color={getTypeColor(notification_type)}
            className="font10 capitalize"
            style={{ margin: 0, flexShrink: 0 }}
          >
            {stringify(notification_type)}
          </Tag>
        </ListItemWrapper>
      );
    };
  
    return {
      ListItem,
      title: NOTIFICATIONS,
      subtitle: "Stay updated on your account activity",
      api: API_NOTIFICATIONS,
      filters,
      viewData,
    };
  };
  
  // Footer: Auto-marks the notification as read as soon as the detail
  // sheet opens (standard read-on-open UX), then shows a confirmation chip.
  // We read the row's id from `state` (the raw notification) because the
  // `data` prop is the post-viewData display object, which has no id.
  const NotificationFooter = ({
    data,
    state,
    dismiss,
  }: {
    data?: any;
    state?: any;
    dismiss?: any;
  }) => {
    const { uploadData, submitting, success } = useSet();

    const row = state || data;

    // Auto-fire mark-as-read once per opened notification.
    // Guarded on row.is_read so we don't waste a request on already-read items
    // and on success so we don't loop on re-renders.
    useEffect(() => {
      if (!row?.id || row.is_read || success || submitting) return;
      uploadData({
        api: `${API_NOTIFICATIONS}${row.id}/read/`,
        method: "POST",
        params: {},
        successMessage: false,
        onSuccess: () => {
          reloadData(NOTIFICATIONS);
          updateGlobalContext({ reload_notifications: new Date() });
        },
      });
    }, [row?.id]);

    if (!row || !row.id) return null;

    // Manual fallback button (kept in case the auto-mark fails)
    const markAsRead = () => {
      uploadData({
        api: `${API_NOTIFICATIONS}${row.id}/read/`,
        method: "POST",
        params: {},
        successMessage: { message: "Marked as read", duration: 2 },
        onSuccess: () => {
          reloadData(NOTIFICATIONS);
          updateGlobalContext({ reload_notifications: new Date() });
          dismiss?.();
        },
      });
    };
  
    // If already read (either was read before, or just got auto-marked),
    // show a subtle confirmation. Otherwise show the manual-mark button.
    if (row.is_read || success) {
      return (
        <div className="p15">
          <Tag
            color="success"
            className="font11"
            style={{ width: "100%", textAlign: "center", padding: "6px 0" }}
            icon={<CheckmarkCircle style={{ marginRight: 6 }} />}
          >
            Marked as read
          </Tag>
        </div>
      );
    }

    return (
      <div className="p15">
        <Button
          className="x100"
          loading={submitting}
          onClick={markAsRead}
          icon={<CheckmarkCircle style={{ marginRight: 6 }} />}
        >
          Mark as Read
        </Button>
      </div>
    );
  };